import { numericUtil } from '@cmg/common';
import type { CellClassRules, DesignSystemTooltipProps } from '@cmg/data-grid';
import { CellClassNames, createValueFormatter } from '@cmg/data-grid';
import isNil from 'lodash/isNil';

import { RevisionTooltipContent } from '../../../../../../common/components/data-grid/RevisionTooltipContent';
import type { RevisionPairs } from '../../../../../../common/util/revision';
import { isRevised } from '../../../../../../common/util/revision';
import { OfferingType } from '../../../../../../graphql/__generated__';
import type { CalendarGridColDef, CalendarGridRowData } from '../../CalendarGrid.model';
import { CALENDAR_GRID_CELL_CLASS_NAMES } from '../../CalendarGrid.model';
import { PriceRangeHeaderTooltipContent } from './PriceRangeHeaderTooltipContent';

export type Value = string;
export type TooltipFiledValue = CalendarGridRowData['attributes'] | string;

const PRICE_RANGE_SEPARATOR = '–';

const getCurrencyPriceRangeValue = (
  low: number | null,
  high: number | null,
  pricingCurrency: string
) => {
  if (isNil(low) || isNil(high)) {
    return '-';
  }

  return [
    numericUtil.getDisplayValueForCurrency(low, 2, pricingCurrency),
    numericUtil.getDisplayValueForCurrency(high, 2, pricingCurrency),
  ].join(PRICE_RANGE_SEPARATOR);
};

type ConvertiblePriceRangeValueArgs = {
  couponTalkPercentageLow: number | null;
  couponTalkPercentageHigh: number | null;
  premiumTalkPercentageLow: number | null;
  premiumTalkPercentageHigh: number | null;
};

const getConvertiblePriceRangeValue = ({
  couponTalkPercentageLow,
  couponTalkPercentageHigh,
  premiumTalkPercentageLow,
  premiumTalkPercentageHigh,
}: ConvertiblePriceRangeValueArgs) => {
  if (
    isNil(couponTalkPercentageLow) &&
    isNil(couponTalkPercentageHigh) &&
    isNil(premiumTalkPercentageLow) &&
    isNil(premiumTalkPercentageHigh)
  ) {
    return '-';
  }

  let couponPriceRanges: string = '-';
  let premiumPriceRanges: string = '-';

  if (!isNil(couponTalkPercentageHigh) && !isNil(couponTalkPercentageLow)) {
    couponPriceRanges = [
      numericUtil.getDisplayValueForPercents(couponTalkPercentageLow, 3),
      numericUtil.getDisplayValueForPercents(couponTalkPercentageHigh, 3),
    ].join(PRICE_RANGE_SEPARATOR);
  }

  if (!isNil(premiumTalkPercentageLow) && !isNil(premiumTalkPercentageHigh)) {
    premiumPriceRanges = [
      numericUtil.getDisplayValueForPercents(premiumTalkPercentageLow, 3),
      numericUtil.getDisplayValueForPercents(premiumTalkPercentageHigh, 3),
    ].join(PRICE_RANGE_SEPARATOR);
  }

  return `${couponPriceRanges} / ${premiumPriceRanges}`;
};

export const valueFormatter = createValueFormatter<CalendarGridRowData, Value>({
  gridRow: ({ data }) => {
    const { type, attributes } = data ?? {};
    const {
      priceRangeLow = null,
      priceRangeHigh = null,
      latestCouponTalkPercentageLow = null,
      latestCouponTalkPercentageHigh = null,
      latestPremiumTalkLowPercentage = null,
      latestPremiumTalkHighPercentage = null,
    } = attributes ?? {};

    const pricingCurrency = attributes?.pricingCurrency ?? 'USD';

    if (type === OfferingType.Convertible) {
      return getConvertiblePriceRangeValue({
        couponTalkPercentageLow: latestCouponTalkPercentageLow,
        couponTalkPercentageHigh: latestCouponTalkPercentageHigh,
        premiumTalkPercentageLow: latestPremiumTalkLowPercentage,
        premiumTalkPercentageHigh: latestPremiumTalkHighPercentage,
      });
    }

    return getCurrencyPriceRangeValue(priceRangeLow, priceRangeHigh, pricingCurrency);
  },
});

const getRevisionPairs = (
  attributes: CalendarGridRowData['attributes'] | undefined
): RevisionPairs => {
  return [
    [attributes?.initialIpoRangeLowUsd, attributes?.revisedIpoRangeLowUsd],
    [attributes?.initialIpoRangeHighUsd, attributes?.revisedIpoRangeHighUsd],
  ];
};

export const getTooltipContent: NonNullable<
  DesignSystemTooltipProps<CalendarGridRowData, TooltipFiledValue>['getContent']
> = ({ value, location }) => {
  if (location === 'header') {
    return () => <PriceRangeHeaderTooltipContent />;
  }

  if (location === 'cell' && typeof value === 'object' && isRevised(getRevisionPairs(value))) {
    const { initialIpoRangeLow = null, initialIpoRangeHigh = null, pricingCurrency } = value ?? {};

    return () => (
      <RevisionTooltipContent
        initialValue={`Initial Range: ${getCurrencyPriceRangeValue(
          initialIpoRangeLow,
          initialIpoRangeHigh,
          pricingCurrency ?? 'USD'
        )}`}
      />
    );
  }
};

export const cellClassRules: CellClassRules<CalendarGridRowData, Value> = {
  [CALENDAR_GRID_CELL_CLASS_NAMES.Revised]: ({ data }) =>
    isRevised(getRevisionPairs(data?.attributes)),
  [CellClassNames.CELL_WITH_TOOLTIP]: ({ data }) => isRevised(getRevisionPairs(data?.attributes)),
};

const priceRangeColDef: CalendarGridColDef<Value> = {
  colId: 'priceRange',
  headerName: 'Price Range',
  sortable: false,
  filter: false,
  width: 136,
  valueFormatter,
  headerTooltip:
    'Price Range will display based off the offering type. IPO: IPO Low–IPO High; FO & Blocks: Re Offer Low–Re Offer High; Converts: Coupon Talk Low–High / Premium Talk Low–High',
  cellClassRules,
  tooltipField: 'attributes',
  tooltipComponentParams: {
    getContent: getTooltipContent,
  },
};

export default priceRangeColDef;
